<template>
  <v-dialog v-model="value" fullscreen hide-overlay>
    <v-card>
      <v-toolbar dark color="primary">
        <v-btn icon dark @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>
          {{ $t("projects.slideManagement.add.title") }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn dark text @click="$emit('input', false)">
            {{ $t("button.close") }}
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text>
        <v-container class="mt-2">
          <v-row>
            <v-col>
              <v-autocomplete
                v-model="filterOrganization"
                :items="organizations"
                :no-data-text="$t('projects.slideManagement.add.organizationEmpty')"
                item-value="id"
                item-text="name"
                :label="$t('projects.slideManagement.add.organizationLabel')"
                :loading="organizationLoading"
                :search-input.sync="searchTermOrganization"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-autocomplete
                :label="$t('projects.slideManagement.add.bodyPartLabel')"
              ></v-autocomplete>
            </v-col>
            <v-col>
              <v-autocomplete
                :label="$t('projects.slideManagement.add.diagnosticsLabel')"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-menu
                v-model="filterMenuDateStart"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="filterDateStart"
                    :label="$t('projects.slideManagement.add.wsiCreatedStart')"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    clearable
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="filterDateStart"
                  @input="filterMenuDateStart = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col>
              <v-menu
                v-model="filterMenuDateEnd"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="filterDateEnd"
                    :label="$t('projects.slideManagement.add.wsiCreatedEnd')"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    clearable
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="filterDateEnd"
                  @input="filterMenuDateEnd = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-btn primary @click="onClickSearch">
                {{ $t("projects.slideManagement.add.search") }}
              </v-btn>
              <v-btn class="ml-2" @click="onClickClearFilter">
                {{ $t("projects.slideManagement.add.clearFilter") }}
              </v-btn>
            </v-col>
          </v-row>

          <TableColumnSelector
            :show="showTableColumnSelector"
            @hide="showTableColumnSelector = false"
            v-model="headers"
            :skip="['in-project', 'data-table-expand']"
          ></TableColumnSelector>

          <v-data-table
            class="table mt-5"
            :headers="selectedHeaders"
            :items="slides"
            :loading="loading"
            :loading-text="$t('projects.slideManagement.add.table.loading')"
            disable-filtering
            @click:row="onClick"
            single-expand
            item-key="id"
            show-expand
            :expanded.sync="expandedRows"
            :no-data-text="noDataText(tableState)"
            :footer-props="footerProps"
            :server-items-length="total"
            :page="currentPage"
            :options.sync="pagingOptions"
            disable-sort
            @update:options="onUpdateOptions"
          >
            <template v-slot:header.data-table-expand>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    small
                    @click.stop.prevent="showTableColumnSelector = true"
                  >
                    mdi-pencil
                  </v-icon>
                </template>
                <span>
                  {{ $t("components.tableColumnSelector.tooltip") }}
                </span>
              </v-tooltip>
            </template>
            <template v-slot:item.scanner="{ item }">
              {{
                $dp.getBySystem(
                  item.modality,
                  "http://deeppath.scian.cl/code-system/modality-manufacturer"
                ).display
              }},
              {{
                $dp.getBySystem(
                  item.modality,
                  "http://deeppath.scian.cl/code-system/modality-model"
                ).display
              }}
            </template>
            <template v-slot:item.organization="{ item }">
              {{ getOrganization(item.organizationResource.reference).name }}
            </template>
            <template v-slot:item.in-project="{ item }">
              <template v-if="item.inProject">
                <v-icon class="green--text" title="WSI already in your project."
                  >mdi-check-circle-outline
                </v-icon>
              </template>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="py-1">
                <detail
                  :item="item"
                  @loading:changed="loading = $event"
                ></detail>
              </td>
            </template>
          </v-data-table>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { fetchImagingStudies, searchOrganization } from "@/api/fhir";
import { mapGetters, mapState } from "vuex";
import TableColumnSelector from "@/components/TableColumnSelector";
import { TABLE_HEADER_SLIDE_MANAGEMENT_ADD_DIALOG } from "@/constants";
import { LIST, SLIDES } from "@/store/modules/slides";
import Detail from "@/components/projects/slide-management/Detail";
import { LOAD } from "@/store/modules/fhir";

export default {
  props: {
    value: Boolean,
    project: Object
  },
  data: () => ({
    dialogButtonCancelDisabled: false,
    loading: false,
    organizations: [],
    endpoints: [],
    tab: 0,
    tableState: null,
    expandedRows: [],
    filterDateStart: null,
    filterDateEnd: null,
    filterMenuDateStart: false,
    filterMenuDateEnd: false,
    organizationLoading: false,
    filterOrganization: null,
    searchTermOrganization: null,
    showTableColumnSelector: false,
    result: null,
    currentPage: 1,
    pagingOptions: null
  }),
  computed: {
    ...mapState({ token: state => state.authentication.keycloak.token }),
    ...mapGetters({ slidesInProject: LIST }),
    footerProps() {
      return {
        "items-per-page-options": [2, 5, 10, 15],
        "items-per-page-text": this.$t("projects.slideManagement.add.table.footerText")
      }
    },
    headers() {
      return [
        { text: this.$t("projects.slideManagement.add.table.id"), value: "id", visible: false },
        { text: this.$t("projects.slideManagement.add.table.date"), value: "started", visible: true },
        { text: this.$t("projects.slideManagement.add.table.scanner"), value: "scanner", visible: true },
        {
          text: this.$t("projects.slideManagement.add.table.organization"),
          value: "organization",
          visible: true
        },
        { text: "", value: "in-project", visible: true },
        { text: "", value: "data-table-expand", visible: true }
      ]
    },
    selectedHeaders() {
      return this.headers.filter(e => e.visible);
    },
    slides() {
      if (!this.result) return [];
      return this.result.imagingStudy;
    },
    total() {
      if (!this.result) return 0;
      return this.result.total;
    },
    resultOrganizations() {
      if (!this.result) return [];
      return this.result.organizations;
    }
  },
  watch: {
    value(val) {
      // when the dialog was closed but then reopened, do not show previous search results
      if (val) {
        this.slides = [];
      }
    },
    headers: {
      deep: true,
      handler(val) {
        this.$dp.store(TABLE_HEADER_SLIDE_MANAGEMENT_ADD_DIALOG, val, true);
      }
    },
    searchTermOrganization(val) {
      this.organizationLoading = true;
      searchOrganization(val)
        .then(res => (this.organizations = res))
        .finally(() => (this.organizationLoading = false));
    }
  },
  methods: {
    noDataText(state) {
      if (state === null)
        return this.$t("projects.slideManagement.add.table.empty");
      else
        return this.$t("projects.slideManagement.add.table.none");
    },
    onClickDialogSave() {},
    onClick(slide) {
      console.log("click, ", slide);
      if (this.expandedRows.map(x => x.id).includes(slide.id)) {
        this.expandedRows = []; // only a single expanded row is allowed, so initialize the array again
      } else {
        this.expandedRows.push(slide);
      }
    },
    getOrganization(reference) {
      const id = reference.split("/")[1];
      return this.resultOrganizations.find(e => e.id === id);
    },
    async onClickSearch() {
      // TODO date validations
      await this.search();
      this.tableState = undefined
    },
    onClickClearFilter() {
      this.filterDateEnd = null;
      this.filterDateStart = null;
      this.filterOrganization = null;
    },
    async onUpdateOptions(val) {
      await this.search(val);
    },
    async search(paging) {
      paging = paging || this.pagingOptions;
      let next = undefined;
      if (this.currentPage < paging.page) {
        next = this.result.next;
      } else {
        if (this.result) {
          next = this.result.previous;
        } else {
          next = undefined;
        }
      }

      this.loading = true;
      this.result = await fetchImagingStudies(
        this.slidesInProject,
        this.filterDateStart,
        this.filterDateEnd,
        this.filterOrganization,
        next,
        paging
      );
      // this.results.forEach(e => {
      //   // TODO load organization resources
      //   this.$store.dispatch(LOAD, e.organizationResource);
      //   console.log("loading org for ", e);
      // });
      this.loading = false;
    }
  },
  async mounted() {
    this.headers = this.$dp.read(
      TABLE_HEADER_SLIDE_MANAGEMENT_ADD_DIALOG,
      this.headers
    );
  },
  components: {
    TableColumnSelector,
    Detail
  }
};
</script>

<style lang="scss" scoped>
.visualization {
  height: 500px;
}
</style>
